<script setup>
import { computed, onMounted, ref } from "vue";
import apiClient from "@/apiClient.js";

const props = defineProps({
  returnId: { type: Number, required: true },
  periodStart: { type: String, required: true },
  periodEnd: { type: String, required: true },
  existingImportId: { type: Number, default: null },
});
const emit = defineEmits(["cancel", "imported"]);
const file = ref(null);
const preview = ref(null);
const matches = ref({});
const loading = ref(false);
const error = ref("");
const notes = ref("");
const checked = ref(false);
const page = ref(1);
const readonly = computed(() => Boolean(preview.value?.confirmed_at));
const visibleRows = computed(
  () => preview.value?.rows.slice((page.value - 1) * 50, page.value * 50) ?? [],
);
const unresolved = computed(
  () => preview.value?.rows.filter((row) => row.problem).length ?? 0,
);
const canConfirm = computed(
  () =>
    preview.value?.ready &&
    !readonly.value &&
    checked.value &&
    notes.value.trim().length >= 10 &&
    !loading.value,
);
const money = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    value / 100,
  );
const usePreview = (data) => {
  preview.value = data;
  matches.value = Object.fromEntries(
    data.rows
      .filter((row) => row.transaction_id)
      .map((row) => [row.row, row.transaction_id]),
  );
};
const fail = (failure) => {
  error.value =
    failure?.response?.data?.message ??
    "Unable to process the historical workbook.";
};
const upload = async () => {
  if (!file.value || loading.value) return;
  loading.value = true;
  error.value = "";
  checked.value = false;
  try {
    const form = new FormData();
    form.append("workbook", file.value);
    const { data } = await apiClient.post(
      `/api/vat-returns/${props.returnId}/imports`,
      form,
      { suppressErrorAlert: true },
    );
    usePreview(data);
    page.value = 1;
  } catch (failure) {
    fail(failure);
  } finally {
    loading.value = false;
  }
};
const refresh = async () => {
  checked.value = false;
  loading.value = true;
  error.value = "";
  try {
    const { data } = await apiClient.post(
      `/api/vat-returns/${props.returnId}/imports/${preview.value?.id ?? props.existingImportId}/preview`,
      { matches: matches.value },
      { suppressErrorAlert: true },
    );
    usePreview(data);
  } catch (failure) {
    fail(failure);
  } finally {
    loading.value = false;
  }
};
const confirm = async () => {
  if (!canConfirm.value) return;
  loading.value = true;
  error.value = "";
  try {
    const { data } = await apiClient.post(
      `/api/vat-returns/${props.returnId}/imports/${preview.value.id}/confirm`,
      {
        matches: matches.value,
        reconciliation_notes: notes.value.trim(),
        filed_records_checked: checked.value,
      },
      { suppressErrorAlert: true },
    );
    emit("imported", data);
  } catch (failure) {
    fail(failure);
  } finally {
    loading.value = false;
  }
};
onMounted(() => {
  if (props.existingImportId) refresh();
});
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/75 px-4 py-8"
    role="dialog"
    aria-modal="true"
    aria-labelledby="historical-vat-title"
  >
    <div
      class="max-h-full w-full max-w-6xl overflow-y-auto rounded-2xl bg-white p-6 text-slate-900 shadow-2xl dark:bg-slate-800 dark:text-slate-100"
    >
      <div class="flex items-start justify-between gap-4">
        <h2 id="historical-vat-title" class="text-lg font-semibold">
          Historical VAT workbook · {{ periodStart }} – {{ periodEnd }}
        </h2>
        <button
          type="button"
          class="btn"
          :disabled="loading"
          @click="emit('cancel')"
        >
          Close
        </button>
      </div>
      <p class="my-3 text-sm">
        Import the workbook used for the filed return. This matches existing
        payments and preserves the workbook's original VAT amounts. It does not
        create payments or submit anything to HMRC. Every row must be reconciled
        before confirmation; closing the quarter is a separate step.
      </p>
      <form
        v-if="!existingImportId && !preview"
        class="flex flex-wrap items-end gap-3"
        @submit.prevent="upload"
      >
        <label
          >Neptune .xlsx workbook<input
            type="file"
            accept=".xlsx"
            aria-label="Historical VAT workbook"
            :disabled="loading"
            @change="file = $event.target.files?.[0] ?? null"
        /></label>
        <button class="btn accent" :disabled="!file || loading">
          Preview workbook
        </button>
      </form>
      <p v-if="loading" class="my-3" role="status">
        Checking workbook and allocations…
      </p>
      <p
        v-if="error"
        class="my-3 text-rose-600 dark:text-rose-300"
        role="alert"
      >
        {{ error }}
      </p>
      <template v-if="preview">
        <p class="my-3 text-sm">
          {{ preview.filename }} · {{ preview.rows.length }} rows ·
          {{ unresolved }} unresolved
        </p>
        <div class="my-3 grid gap-3 sm:grid-cols-3">
          <div>
            Filed input VAT
            <strong class="block">{{ money(preview.input_vat) }}</strong>
          </div>
          <div>
            Filed output VAT
            <strong class="block">{{ money(preview.output_vat) }}</strong>
          </div>
          <div>
            Filed liability / repayment
            <strong class="block">{{ money(preview.liability) }}</strong>
          </div>
        </div>
        <p class="my-3 text-sm">
          Check these totals against the actual filed return. Matches based on
          date, amount and description still require your review. If a payment
          is missing, reconcile/import it through bank transactions first, then
          reopen this preview.
        </p>
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="text-left">
              <tr>
                <th class="p-2">Workbook row</th>
                <th class="p-2">Date / description</th>
                <th class="p-2">Gross / VAT</th>
                <th class="p-2">Existing payment</th>
                <th class="p-2">Result</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in visibleRows"
                :key="row.row"
                class="border-t border-slate-200 dark:border-slate-700"
              >
                <td class="p-2">{{ row.row }} · {{ row.kind }}</td>
                <td class="p-2">{{ row.date }}<br />{{ row.description }}</td>
                <td class="p-2 whitespace-nowrap">
                  {{ money(row.gross) }}<br />{{ money(row.vat) }}
                </td>
                <td class="p-2">
                  <template v-if="readonly"
                    >{{ row.source }} #{{ row.transaction_id }}</template
                  >
                  <select
                    v-else
                    v-model="matches[row.row]"
                    class="input max-w-sm"
                    :aria-label="`Match workbook row ${row.row}`"
                    :disabled="loading"
                    @change="refresh"
                  >
                    <option :value="null">Choose a payment</option>
                    <option
                      v-for="candidate in row.candidates"
                      :key="candidate.id"
                      :value="candidate.id"
                    >
                      {{ row.source }} #{{ candidate.id }} ·
                      {{ candidate.description }} ·
                      {{ money(candidate.amount) }}
                    </option>
                  </select>
                </td>
                <td
                  class="p-2"
                  :class="row.problem ? 'text-rose-600 dark:text-rose-300' : ''"
                >
                  {{ row.problem || "Matched — review against original books" }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          v-if="preview.rows.length > 50"
          class="my-3 flex items-center justify-between"
        >
          <button class="btn" :disabled="page === 1 || loading" @click="page--">
            Previous</button
          ><span
            >Page {{ page }} of {{ Math.ceil(preview.rows.length / 50) }}</span
          ><button
            class="btn"
            :disabled="page * 50 >= preview.rows.length || loading"
            @click="page++"
          >
            Next
          </button>
        </div>
        <div
          v-if="!readonly"
          class="mt-4 space-y-3 rounded border border-amber-400 p-4"
        >
          <label class="block text-sm"
            >Reconciliation notes<textarea
              v-model="notes"
              aria-label="Import reconciliation notes"
              class="input mt-1 w-full"
              maxlength="2000"
              :disabled="loading"
              placeholder="Record the filed return/workbook checked and explain any manual matches."
            />
          </label>
          <label class="flex items-start gap-2 text-sm"
            ><input
              v-model="checked"
              type="checkbox"
              aria-label="Confirm workbook matches the filed return"
              :disabled="loading"
            />
            I checked every row and the totals against the filed return and
            original books. These payments belong to this quarter and have not
            been claimed on another return.</label
          >
          <p class="text-sm">
            Confirmation preserves these allocations and filed totals. They
            cannot be replaced by another workbook through this importer.
          </p>
          <button
            type="button"
            class="btn accent"
            :disabled="!canConfirm"
            @click="confirm"
          >
            Confirm historical import
          </button>
        </div>
      </template>
    </div>
  </div>
</template>
